import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSummary, getAttendanceMonth, getLeaveMonth } from '../lib/db.js';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { thaiDate } from '../lib/format.js';

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const NOW_YM = '2026-06'; // demo "today"

export function AttendanceMonth() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ym, setYm] = useState(NOW_YM);
  const [name, setName] = useState('');
  const [flags, setFlags] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    getSummary(id).then((s) => setName(s ? (s.nickname || s.first_name) : '')).catch(() => {});
  }, [id]);

  useEffect(() => {
    let active = true;
    setFlags(null);
    (async () => {
      try {
        const [f, l] = await Promise.all([getAttendanceMonth(id, ym), getLeaveMonth(id, ym)]);
        if (!active) return;
        setFlags(f);
        setLeaves(l);
      } catch (e) { if (active) setErr(e.message); }
    })();
    return () => { active = false; };
  }, [id, ym]);

  const stat = useMemo(() => {
    const f = flags || [];
    const faults = f.filter((x) => !x.is_excused);
    const late = faults.filter((x) => x.flag_type === 'late');
    const absent = faults.filter((x) => x.flag_type === 'absent');
    const early = faults.filter((x) => x.flag_type === 'early_leave');
    const excused = f.filter((x) => x.is_excused);
    return {
      faults, late, absent, early, excused,
      lateMins: late.reduce((a, x) => a + (x.minutes || 0), 0),
      earlyMins: early.reduce((a, x) => a + (x.minutes || 0), 0),
      leaveDays: leaves.reduce((a, l) => a + (Number(l.days) || 0), 0),
    };
  }, [flags, leaves]);

  const [y, m] = ym.split('-').map(Number);
  const monthLabel = `${TH_MONTHS[m - 1]} ${(y + 543) % 100}`;
  const atNow = ym >= NOW_YM;

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        <div className="title-row">
          <div>
            <div className="eyebrow">สถิติเข้างาน{name ? ` · ${name}` : ''}</div>
            <h1 className="page">รายเดือน</h1>
          </div>
          <span className="glyph">◷</span>
        </div>

        <div className="monthnav">
          <button onClick={() => setYm(addMonth(ym, -1))} aria-label="เดือนก่อน">‹</button>
          <span className="m">{monthLabel}</span>
          <button onClick={() => setYm(addMonth(ym, 1))} disabled={atNow} aria-label="เดือนถัดไป">›</button>
        </div>

        {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}
        {!flags && !err && <div className="center"><div className="spinner" /></div>}

        {flags && (
          <>
            <div className="hero">
              <div className="num small">{stat.faults.length}</div>
              <div className="sub">ความผิดด้านเวลาเดือนนี้ (ไม่รวมวันลา)</div>
            </div>

            <div className="block-fault">
              <div className="sec-label">ความผิด (จริง)</div>
              <div className="grid">
                <Stat pip="warn" k="มาสาย" v={stat.late.length} unit={`ครั้ง · ${stat.lateMins} นาที`} />
                <Stat pip="warn" k="ขาดงาน" v={stat.absent.length} unit="ครั้ง" />
                <Stat pip="warn" k="ออกก่อนเวลา" v={stat.early.length} unit={`ครั้ง · ${stat.earlyMins} นาที`} />
                <Stat k="ปรับเป็นลา (ไม่นับ)" v={stat.excused.length} unit="วัน" />
              </div>
            </div>

            <div className="block-right">
              <div className="sec-label">สิทธิ (ลา) · {stat.leaveDays} วัน</div>
              {leaves.length === 0 && <p className="muted">ไม่มีการลาในเดือนนี้</p>}
              {leaves.map((l) => (
                <div className="row" key={l.id}>
                  <div className="ic" style={{ color: 'var(--ok)' }}>✓</div>
                  <div className="main">
                    <div className="l1">{leaveName(l)}</div>
                    <div className="l2">{thaiDate(l.start_date)}{l.end_date !== l.start_date ? `–${thaiDate(l.end_date)}` : ''}</div>
                  </div>
                  <span className="tag ok">{l.days} วัน</span>
                </div>
              ))}
            </div>

            <div className="sec-label">รายวัน</div>
            {flags.length === 0 && <p className="muted">ไม่มีรายการในเดือนนี้</p>}
            {flags.map((a) => (
              <div className="row" key={a.id}>
                <div className="ic">{glyph(a.flag_type)}</div>
                <div className="main">
                  <div className="l1">{label(a)}</div>
                  <div className="l2">{thaiDate(a.work_date)}</div>
                </div>
                {a.is_excused
                  ? <span className="tag ok">ลา · ไม่นับ</span>
                  : <span className="tag warn">ความผิด</span>}
              </div>
            ))}
          </>
        )}
      </section>
      <BottomNav />
    </div>
  );
}

function Stat({ pip, k, v, unit }) {
  return (
    <div className="stat">
      <div className="k">{pip && <span className={`pip ${pip}`} />}{k}</div>
      <div className="v">{v} <small>{unit}</small></div>
    </div>
  );
}
function addMonth(ym, d) {
  let [y, m] = ym.split('-').map(Number);
  m += d;
  if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}
const leaveName = (l) => l.name_th || l.leave_types?.name_th || 'ลา';
const glyph = (t) => ({ late: '◔', absent: '○', early_leave: '◑' }[t] || '·');
const label = (a) => {
  if (a.flag_type === 'late') return `มาสาย ${a.minutes} นาที`;
  if (a.flag_type === 'early_leave') return `ออกก่อนเวลา ${a.minutes} นาที`;
  if (a.flag_type === 'absent') return 'ขาดงาน';
  return a.flag_type;
};
